import { TextOnlyButton } from '@rbilabs/components-library/build/components/button';
import styled, { css } from 'styled-components';

import { IconLoyaltyPoints } from 'components/icons/loyalty-points-icon';
import { Badge } from 'components/offer-badges/badge';
import { Picture } from 'components/picture';
import { ProgressMeter } from 'components/progress-meter';
import { StyledBlockRenderer } from 'pages/loyalty/loyalty-offers/loyalty-offers.styled';
import { UpcomingOfferBadge } from 'pages/loyalty/upcoming-offer/upcoming-offer-badge';
import { primitive } from 'styles/constants/primitives';
import { fadeIn } from 'utils/style/animations';

import { RewardLock } from '../../reward-lock';
import { HIGHLIGHT_COLOR } from '../constants';
import { RewardLockIcon } from '../reward-lock-icon';

export const REWARD_CARD_HEIGHT = 8.25;

export const RewardText = styled.h3`
  font-size: 1rem;
  font-family: ${Styles.fontFamily.brand};
  font-weight: ${Styles.fontWeight.normal};
  letter-spacing: normal;
  line-height: 1.25;
  margin-block-start: 0;
  margin-block-end: 0.25rem;
  margin-inline: 0;
`;

export const BackgroundImage = styled(Picture)`
  width: 100%;
  height: 100%;
  position: absolute;
`;

export const DefaultBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  background: ${Styles.color.background};
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 9.375rem;
  height: 6.25rem;
  flex-shrink: 0;
  z-index: ${Styles.zIndex.below};
  ${Styles.mobileTiny} {
    width: 7rem;
  }
`;

export const StyledProgressMeter = styled(ProgressMeter)`
  border: 0;
  background: white;
  width: 95%;
  margin: 0.25rem 0;
  ${Styles.desktop} {
    width: 80%;
  }
`;

export const StyledRewardPoints = styled.p`
  font-size: 1rem;
  margin-block-start: 0.25rem;
  margin-block-end: 0;
  margin-inline: 0;
  text-transform: lowercase;
  strong {
    font-weight: ${Styles.fontWeight.heavy};
    color: ${Styles.color.primary};
  }
`;

export const CardContainer = styled.li`
  height: 100%;
  width: 100%;
  padding: 0;
  background: ${primitive.$white};
`;

export const StyledClickableContainer = styled.button<{
  isLocked: boolean;
  isQuestChallengeIcentive?: boolean;
}>`
  /* reset button styles */
  border: none;
  text-align: start;
  background: ${primitive.$white};
  display: flex;

  width: 100%;
  height: 100%;
  opacity: 0;
  animation: ${fadeIn} 0.4s forwards;
  border-block-end: 1px solid ${Styles.color.grey.four};
  padding: 1rem;
  cursor: pointer;
  position: relative;
  z-index: ${Styles.zIndex.normal};
  ${StyledRewardPoints} {
    strong {
      ${p =>
        p.isLocked &&
        css`
          color: ${Styles.color.grey.three};
        `}
    }
  }
  ${ImageWrapper} {
    img {
      ${p =>
        p.isQuestChallengeIcentive &&
        css`
          opacity: 0.5;
        `}
    }
  }
`;

export const Overlay = styled.div`
  opacity: 0.275;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: ${Styles.zIndex.normal};
  background-color: ${Styles.color.grey.three};
`;

export const InfoContainer = styled.div`
  margin-inline-start: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

export const PointsNeededContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const PointsNeededText = styled.p`
  font-size: 0.8125rem;
  margin: 0;

  ${Styles.desktop} {
    font-size: 1rem;
  }

  span {
    color: ${Styles.color.primary};
  }
`;

export const StyledLockIcon = styled(RewardLockIcon)`
  & > div {
    inset-block-start: 3px;
    transform: scale(0.4);
  }
`;

export const PointTextContainer = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.25;
`;

export const StyledPointsIcon = styled(IconLoyaltyPoints)`
  margin-inline-end: 0.25em;
`;

export const HighlightedPointsIcon = styled(StyledPointsIcon)`
  path {
    fill: ${HIGHLIGHT_COLOR};
  }
`;

export const StyledUnLockedRewardPoints = styled.div`
  display: none;
`;

export const StyledRewardLock = styled(RewardLock)`
  > div {
    background-color: ${Styles.color.tertiary};
    ::before,
    ::after {
      border-color: ${Styles.color.tertiary};
    }
    ::after {
      background-color: inherit;
    }
  }
`;

export const PointsNeededLockIcon = styled(StyledRewardLock)`
  transform: scale(0.4);
  position: relative;
  width: 1.2rem;
  height: 1.2rem;
  margin-block: 0;
  margin-inline-start: 0;
  margin-inline-end: 0.25rem;
  display: none;
  > div {
    background-color: ${HIGHLIGHT_COLOR};
    ::before,
    ::after {
      border-color: ${HIGHLIGHT_COLOR};
    }
    ::after {
      background-color: inherit;
    }
  }
`;

export const IncentiveCardDescription = styled(StyledBlockRenderer)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  max-height: 2rem;
`;

export const StyledBadge = styled(Badge)`
  display: flex;
  border: 1px solid black;
  border-radius: 0.35rem;
  width: fit-content;
  margin-block-start: 0.8rem;
  width: fit-content;
  font-weight: normal;
  font-size: 0.8rem;
  & > * {
    margin-inline-end: 0.3rem;
  }
`;

export const WrapperButton = styled(TextOnlyButton)`
  border-block-end: none;
  border-radius: 50%;

  &:focus {
    border-block-end: none;
  }
`;

export const LoadingWrapper = styled.div`
  height: 2.125rem;
`;

export const StyledUpcomingOfferBadge = styled(UpcomingOfferBadge)`
  margin-block-start: 1rem;
`;
